import React from 'react';

import { type UUID, isDefined, isEmpty, pickFrom } from '@nfctron/nexus-core';
import { UiButton, UiDrawer, UiDrawerContent, UiDrawerFooter, UiDrawerHeader, UiDrawerTitle, useFormat } from '@nfctron/nexus-react';

import { Text } from '@mantine/core';
import { CheckIcon } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Form } from '@root/features/form/components/Form/Form';
import { type FormProps } from '@root/features/form/components/Form/Form.types';

import { Gallery } from '@root/lib/components/Gallery/Gallery';
import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type AtomComponent } from '@root/lib/types/components';

import { ProductGroupController, type ProductGroupControllerOption } from '../../components/Product/ProductGroupController';
import { ProductVariantController, type ProductVariantControllerOption } from '../../components/Product/ProductVariantController';
import {
	type CartStore,
	type CartStoreCartedTicketsSlice,
	type CartStoreExtraProductsSlice,
	useCartStore,
	useCartStoreExtraProductsProvider,
} from '../../stores/cartStore';
import { ProductConfigurationForm } from '../ProductConfigurationForm';

/**
 * Event/Cart/ConfigSheetVariantsAtom atom
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const ConfigSheetVariantsAtom: AtomComponent<ConfigSheetVariantsAtomProps> = ({ open, dismiss, input }) => {
	const { t } = useTranslate(APP_I18N_NAMESPACE.MAIN, ConfigSheetVariantsAtom.displayName);
	const format = useFormat();

	const event = useCartStore((s) => s.cartConfig);
	const extraProductsProvider = useCartStoreExtraProductsProvider();

	/** get current carted ticket and its ticket type config */
	const { ticket, ticketType } = useCartStore((s) => s.getCartedTicket({ cartTicketId: input.cartTicketId }));

	/** format and memoize product group related data */
	const group = React.useMemo(() => {
		/** get product group config */
		const productGroup = extraProductsProvider.groupGetters.getById({
			productGroupId: input.productGroupId,
		});
		/** get product group products */
		const productGroupProducts = extraProductsProvider.groupGetters.getProducts({
			productGroupId: productGroup.productGroupId,
			ticketTypeId: ticket.ticketTypeId,
		});
		return { config: productGroup, products: productGroupProducts };
	}, [extraProductsProvider.groupGetters, input.productGroupId, ticket.ticketTypeId]);

	/**
	 * Get the actual carted product if provided from the input
	 * @type {UseCartTicketsTicketExtraProduct | undefined}
	 */
	const updatedProduct = React.useMemo(
		() => ticket.extraProducts.find((ep) => ep.cartProductId === input.cartProductId),
		[input.cartProductId, ticket.extraProducts],
	);

	/**
	 * Returns all possible products with the given combination.
	 * Can decide whether to include unavailable (soldOut or quantityLimited) products.
	 * @type {(_combinations: UseCartExtraProductsExtraProductVariantCombination, withUnavailable?: boolean) => (UseCartExtraProductsExtraProduct & {quantityLimit: number})[]}
	 */
	const getPossibleProductsFromCombinations = React.useCallback(
		(_combinations: CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo, withUnavailable: boolean = true) =>
			/** find all possible products matching the new combinations */
			group.products
				.filter((product) => {
					/** from the new combinations, match product that contains every one of the given combinations */
					const isMatching = Object.entries(_combinations).every(([cVariantId, cValueId]) =>
						Object.entries(product.variants).some(([pVariantId, pValueId]) => pVariantId === cVariantId && pValueId === cValueId),
					);

					/** if is not matching, return false */
					if (!isMatching) return false;

					/** matching, return early depending on the presence of unavailable products */
					if (withUnavailable) return true;

					/** return only available products as requested */
					const productMeta = extraProductsProvider.productGetters.getState({
						ticketProductId: product.ticketProductId,
						cartTicketId: input.cartTicketId,
						cartProductId: input.cartProductId,
					});
					return !productMeta.soldOut && !productMeta.quantityLimited;
				})
				/**
				 * Map the products to include __only variants from the current group__,
				 * as it can contain more unrelated to the current group and will break in the variants length check later.
				 */
				.map((product) => {
					return {
						...product,
						variants: Object.fromEntries(
							Object.entries(product.variants).filter(([variantId]) => {
								return group.config.variants.some((v) => v.variantId === variantId);
							}),
						),
					};
				}),
		[extraProductsProvider.productGetters, group.config.variants, group.products, input.cartProductId, input.cartTicketId],
	);

	/**
	 * Returns initial combinations for the given product group.
	 * Automatically sets initially selected values from configuration.
	 * If already carted product is provided, it will be used to set initial values.
	 * @type {() => UseCartExtraProductsExtraProductVariantCombination}
	 */
	const getInitialCombinations = React.useCallback<() => CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo>(() => {
		/** return updated product variants, if updating */
		if (isDefined(updatedProduct)) {
			const extraProduct = extraProductsProvider.productGetters.getById({
				ticketProductId: updatedProduct.ticketProductId,
			});
			return extraProduct.variants;
		}

		/** return default config variants */
		return group.config.variants.reduce((acc, variant) => {
			/** create the possible combination */
			const possibleCombination = {
				...acc,
				[variant.variantId]: variant.values.find((val) => val.selected)?.valueId ?? null,
			};
			/** find if the combination matches any _available_ products */
			const possibleProducts = getPossibleProductsFromCombinations(possibleCombination, false);
			return possibleProducts.length > 0 ? possibleCombination : acc;
		}, {});
	}, [extraProductsProvider.productGetters, getPossibleProductsFromCombinations, group.config.variants, updatedProduct]);

	/** store variant combinations */
	const [combinations, setCombinations] = React.useState<CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo>(() => getInitialCombinations());
	/** set initial combinations upon product group variants change */
	React.useEffect(() => {
		setCombinations(getInitialCombinations());
	}, [getInitialCombinations]);

	/**
	 * Handler for when a variant value is selected.
	 * @type {(value: (string | number), variantId: UUID) => void}
	 */
	const onVariantValueChange = React.useCallback(
		(value: string | number, variantId: UUID) => {
			/** get ref variant index */
			const variantIndex = group.config.variants.findIndex((v) => v.variantId === variantId);

			/** construct possibly new combinations */
			const possiblyNewCombinationsWithMatch: CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo = {
				...combinations,
				[variantId]: Number(value),
			};

			/** check if it still matches any _available_ products  */
			const prods = getPossibleProductsFromCombinations(possiblyNewCombinationsWithMatch, false);
			if (prods.length > 0) {
				setCombinations(possiblyNewCombinationsWithMatch);
			} else {
				/** unset any values after the current one */
				setCombinations({
					/** keep only previously selected values */
					...pickFrom(combinations, ...group.config.variants.slice(0, variantIndex).map((v) => v.variantId)),
					/** set the new value */
					[variantId]: Number(value),
				});
			}
		},
		[combinations, getPossibleProductsFromCombinations, group.config.variants],
	);

	/**
	 * Returns grouped products options
	 * @type {() => ProductGroupControllerOption[]}
	 */
	const getProductGroupControllerOptions = React.useCallback<() => ProductGroupControllerOption[]>(
		() =>
			group.products.map((product) => ({
				product,
				productMeta: extraProductsProvider.productGetters.getState({
					ticketProductId: product.ticketProductId,
					cartTicketId: input.cartTicketId,
					cartProductId: input.cartProductId,
				}),
			})),
		[extraProductsProvider.productGetters, group.products, input.cartProductId, input.cartTicketId],
	);

	/**
	 * Returns product group variant options
	 * @type {(variantId: UUID) => ProductVariantControllerOption[]}
	 */
	const getProductVariantOptions = React.useCallback<(variantId: UUID) => ProductVariantControllerOption[]>(
		(variantId: UUID) => {
			/** get ref variant config */
			const variantIndex = group.config.variants.findIndex((v) => v.variantId === variantId);
			if (variantIndex === -1) throw new Error(`Variant with id "${variantId}" not found`);
			const variant = group.config.variants[variantIndex]!;

			return variant.values.reduce((acc, value) => {
				const isActive = combinations[variantId] === value.valueId;

				/** previous variants, ignoring variants after the current */
				const already = group.config.variants.slice(0, variantIndex).map((v) => v.variantId);
				/** construct new combinations */
				const newCombinations: CartStoreExtraProductsSlice.ExtraProductConfigVariantCombo = {
					/** use values from previous variant values */
					...pickFrom(combinations, ...already),
					/** for current variant, set the given value */
					[variantId]: value.valueId,
				};

				/** get all possible products for the new combination */
				const possibleProducts = getPossibleProductsFromCombinations(newCombinations);

				/** do not display value that points to no products, that can be selected with the given combination */
				if (possibleProducts.length === 0) return acc;

				/** given value points to a single specific product with full combination match */
				const firstFoundProduct = possibleProducts.length === 1 ? possibleProducts[0] : null;
				const foundProduct =
					isDefined(firstFoundProduct) && Object.values(firstFoundProduct.variants).length === Object.values(newCombinations).length
						? firstFoundProduct
						: null;

				/** for the found product, get its meta */
				const foundProductMeta = isDefined(foundProduct)
					? extraProductsProvider.productGetters.getState({
							ticketProductId: foundProduct.ticketProductId,
							cartTicketId: input.cartTicketId,
							cartProductId: input.cartProductId,
						})
					: undefined;

				return [
					...acc,
					{
						newCombinations,
						possibleProducts,
						foundProduct,
						foundProductMeta,
						isActive,
						value,
					},
				];
			}, [] as ProductVariantControllerOption[]);
		},
		[
			combinations,
			extraProductsProvider.productGetters,
			getPossibleProductsFromCombinations,
			group.config.variants,
			input.cartProductId,
			input.cartTicketId,
		],
	);

	/** product form */
	const productForm = useForm<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>({
		mode: 'all',
		criteriaMode: 'firstError',
		shouldFocusError: true,
		reValidateMode: 'onChange',
		defaultValues: updatedProduct,
	});

	/** watch form values */
	const values = useWatch<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>({ control: productForm.control });

	/** construct common current combinations to work with */
	const current = React.useMemo(() => {
		if (isEmpty(values.ticketProductId)) return null;

		/** get current extra product config */
		const extraProductConfig = extraProductsProvider.productGetters.getById({
			ticketProductId: values.ticketProductId,
		});

		/** get current extra product meta */
		const extraProductMeta = extraProductsProvider.productGetters.getState({
			ticketProductId: extraProductConfig.ticketProductId,
			cartTicketId: input.cartTicketId,
			cartProductId: input.cartProductId,
		});
		return {
			config: extraProductConfig,
			meta: extraProductMeta,
		};
	}, [extraProductsProvider.productGetters, input.cartProductId, input.cartTicketId, values.ticketProductId]);

	/**
	 * Form submit handler
	 * @type {(data: UseCartTicketsTicketExtraProduct, event?: React.BaseSyntheticEvent) => any}
	 */
	const onSubmitHandler = React.useCallback<FormProps<CartStoreCartedTicketsSlice.CartedTicketExtraProduct>['onSubmit']>(
		(fields) => {
			if (!isDefined(current)) return;

			if (isDefined(updatedProduct)) {
				/** update existing carted product */
				extraProductsProvider.cartOps.update({
					cartProductId: updatedProduct.cartProductId,
					cartTicketId: input.cartTicketId,
					update: fields,
				});
			} else {
				/** check if the product is with advanced configuration and is not carted to create a new instance, otherwise just update the existing product instance */
				const withConfig = extraProductsProvider.productGetters.isConfigRequired({
					ticketProductId: fields.ticketProductId,
					ticketTypeId: ticket.ticketTypeId,
				});
				const alreadyCarted = ticket.extraProducts.find((ep) => ep.ticketProductId === fields.ticketProductId);

				if (alreadyCarted && !withConfig) {
					/** if already carted and without config, update current product's instance quantity */
					extraProductsProvider.cartOps.update({
						cartProductId: alreadyCarted.cartProductId,
						cartTicketId: input.cartTicketId,
						update: { count: alreadyCarted.count + 1 },
					});
				} else {
					/** create new product and add it to the cart */
					extraProductsProvider.cartOps.add({
						ticketProductId: fields.ticketProductId,
						cartTicketId: input.cartTicketId,
						initialValue: fields,
					});
				}
			}

			/** reset the combinations */
			setCombinations(getInitialCombinations());

			/** close the sheet */
			dismiss();
		},
		[
			current,
			dismiss,
			extraProductsProvider.cartOps,
			extraProductsProvider.productGetters,
			getInitialCombinations,
			input.cartTicketId,
			ticket.extraProducts,
			ticket.ticketTypeId,
			updatedProduct,
		],
	);

	/** reset form values upon combinations change */
	React.useEffect(() => {
		const cartProductId = productForm.getValues().cartProductId || uuidv4();

		/** get the matching product */
		const foundProducts = getPossibleProductsFromCombinations(combinations);
		const firstFoundProduct = foundProducts.length === 1 ? foundProducts[0] : null;
		const foundProduct =
			isDefined(firstFoundProduct) && Object.values(firstFoundProduct.variants).length === Object.values(combinations).length
				? firstFoundProduct
				: null;

		if (isDefined(foundProduct)) {
			if (foundProduct?.ticketProductId === productForm.getValues().ticketProductId) {
				/** reset the form with an existing product values */
				productForm.reset(productForm.getValues());
			} else {
				/** reset the form with a new product */
				productForm.reset({
					cartProductId,
					ticketProductId: foundProduct.ticketProductId,
					count: 1,
					optionalText: '',
					chips: extraProductsProvider.productGetters.getInitialChips({
						ticketProductId: foundProduct.ticketProductId,
						ticketTypeId: ticket.ticketTypeId,
					}),
				});
			}
		} else {
			/** set a new cart product instance without selected `ticketProductId` */
			productForm.reset({
				cartProductId,
				ticketProductId: '',
				count: 1,
				optionalText: '',
				chips: [],
			});
		}
	}, [combinations, extraProductsProvider.productGetters, getPossibleProductsFromCombinations, productForm, ticket.ticketTypeId]);

	/** decide whether to show product variants or grouped products */
	const withVariants = React.useMemo(() => group.config.variants.length > 0, [group.config.variants]);

	return (
		<UiDrawer open={open} onClose={dismiss}>
			<UiDrawerContent className="mx-auto max-w-screen-sm">
				<UiDrawerHeader className="flex justify-center border-b border-zinc-200 py-6">
					<UiDrawerTitle asChild>
						<Text fw={700} c="lucky-point" fz={18}>
							{t('header')}
						</Text>
					</UiDrawerTitle>
				</UiDrawerHeader>

				<Form withInvisibleSubmit form={productForm} onSubmit={onSubmitHandler} className="flex max-h-full flex-col gap-4 overflow-scroll p-4">
					{/* product group info section */}
					<section className="flex flex-col gap-4">
						{/* gallery */}
						{isDefined(group.config.gallery) && <Gallery gallery={group.config.gallery} height={200} />}

						{/* text */}
						<div>
							{/* name */}
							<Text c="lucky-point" className="text-2xl">
								{group.config.name}
							</Text>
							{/* description */}
							<Text size="md" c="dimmed">
								{group.config.description ?? t('noDescription.text')}
							</Text>
						</div>
					</section>

					{/* product group/variant controllers */}
					{withVariants ? (
						/** product variants controller */
						<section className="flex flex-col gap-4">
							{group.config.variants.map((variant) => {
								/** get variantOptions for the given variant */
								const variantOptions = getProductVariantOptions(variant.variantId);

								/** for no variantOptions, hide whole variant section */
								if (variantOptions.length === 0) return null;

								return (
									<div key={variant.variantId} className="flex flex-col gap-2">
										{/* variant name */}
										<Text fw={700} c="dimmed" size="md">
											{variant.name}
										</Text>

										{/* variant description */}
										{!isEmpty(variant.description) && (
											<Text size="md" c="dimmed">
												{variant.description}
											</Text>
										)}

										{/* variant gallery */}
										{isDefined(variant.gallery) && <Gallery gallery={variant.gallery} height={150} />}

										{/* variant controller */}
										<div>
											<ProductVariantController
												value={combinations[variant.variantId]!}
												onChange={(value) => onVariantValueChange(value, variant.variantId)}
												options={variantOptions}
												currencyId={event.currencyId}
												productGroupVariantControlType={variant.controlTypeId}
											/>
										</div>
									</div>
								);
							})}
						</section>
					) : (
						/** grouped products controller */
						<section>
							<ProductGroupController
								options={getProductGroupControllerOptions()}
								currencyId={event.currencyId}
								value={values.ticketProductId}
								onChange={(value) =>
									productForm.reset({
										...values,
										ticketProductId: value,
										count: 1,
										optionalText: '',
										chips: extraProductsProvider.productGetters.getInitialChips({
											ticketProductId: value,
											ticketTypeId: ticket.ticketTypeId,
										}),
									})
								}
							/>
						</section>
					)}

					{/* product config section */}
					{isDefined(current) && !current.meta.soldOut && !current.meta.quantityLimited && (
						<ProductConfigurationForm extraProductConfig={current.config} ticketTypeConfig={ticketType} form={productForm} />
					)}
				</Form>

				<UiDrawerFooter>
					<div className="flex items-center gap-3">
						{isDefined(current) ? (
							<React.Fragment>
								{current.meta.soldOut ? (
									<UiButton type="submit" variant="primary" fullWidth disabled>
										{t('soldOutButton.text')}
									</UiButton>
								) : current.meta.quantityLimited ? (
									<UiButton type="submit" variant="primary" fullWidth disabled>
										{t('quantityLimitedButton.text')}
									</UiButton>
								) : (
									<UiButton
										type="submit"
										onClick={productForm.handleSubmit(onSubmitHandler)}
										variant="primary"
										fullWidth
										leftSection={<CheckIcon />}
										disabled={!productForm.formState.isValid}
									>
										{!isEmpty(input.cartProductId)
											? t('updateButton.text', {
													price: format.price(
														extraProductsProvider.cartOps.getPrice({
															ticketProductId: values.ticketProductId,
															cartProductId: input.cartProductId,
															cartTicketId: input.cartTicketId,
														}),
														event.currencyId,
													),
												})
											: t('createButton.text', {
													price: format.price(
														extraProductsProvider.cartOps.getPrice({
															ticketProductId: values.ticketProductId,
															cartProductId: undefined,
															cartTicketId: input.cartTicketId,
														}),
														event.currencyId,
													),
												})}
									</UiButton>
								)}
							</React.Fragment>
						) : (
							/** default disabled button */
							<UiButton fullWidth disabled>
								{t('defaultButton.text')}
							</UiButton>
						)}
					</div>
				</UiDrawerFooter>
			</UiDrawerContent>
		</UiDrawer>
	);
};

/**
 * Event/Cart/ConfigSheetVariants atom props type
 * @export
 */
export type ConfigSheetVariantsAtomProps = {
	open: boolean;
	dismiss: () => void;
	input: NonNullable<CartStore.Store['extraProductConfigSheetVariantsInput']>;
};

/**
 * Event/Cart/ConfigSheetVariantsAtom atom display name
 * @type {ComponentDisplayName<"atom">>}
 */
ConfigSheetVariantsAtom.displayName = 'Event/Cart/ConfigSheetVariantsAtom';
